import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import "../../Project.scss";
import {
  ViewProjectAssignedTo,
  ViewProject as ViewProjectPermission,
  EditProject,
  DeleteProject,
  DeleteProjectIfLead,
  ManageDeletedData,
  EditProjectAssignedTo,
} from "../../../../services/authorization/Permissions";
import {
  ActionsSection,
  ButtonsSection,
  DataSection,
  SubSection,
  DescriptionSection,
} from "../../../../components/data-components/sections/DataSections";
import {
  DisplayProjectName,
  DisplayProjectStatus,
  DisplayProjectLead,
  DisplayProjectCodeSystem,
  DisplayProjectFormat,
  DisplayReportTemplate,
} from "../../components/DataComponents";
import { ProjectMenuItemsModel } from "../../ProjectMenuItems";
import EditButton from "../../../../components/buttons/edit-button/EditButton";
import DeleteButton from "../../../../components/buttons/delete-button/DeleteButton";
import PageLayout from "../../../_layout/PageLayout";
import DisplayAvailableFrom from "../../../../components/data-components/DisplayAvailableFrom";
import DisplayDeadline from "../../../../components/data-components/DisplayDeadline";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { DisplayCoordinatesSystem } from "../../../settings/gis-coordinates/components/DataComponents";
import { CustomProjectPropertiesSection } from "./components/DataComponents";
import { CurrentProjectStorageModel } from "../../../../services/project/CurrentProjectStorage";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { currentUserService } from "../../../../services/user/CurrentUserStorage";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import { featureFlagsStorage } from "../../../../services/feature-flags/FeatureFlagsStorage";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { Flags } from "../../../../generated/FeatureFlags";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();

  const currentProjectId = urlParamsService.getProjectId()!;

  const [currentProjectStorage] = useState(() =>
    CurrentProjectStorageModel.create({ projectId: currentProjectId }, { api })
  );

  const projectMenuItems = useMemo(() => {
    return ProjectMenuItemsModel.create({
      projectId: currentProjectId,
      isLoading: currentProjectStorage.isLoading,
      numberOfSewerObjects: currentProjectStorage.currentProject.sewerObjectsCount,
    });
  }, [
    currentProjectId,
    currentProjectStorage.currentProject.sewerObjectsCount,
    currentProjectStorage.isLoading,
  ]);

  const canUserEditProject = useMemo(() => {
    return permissionsService.userHasOneOfThePermissions([EditProject, EditProjectAssignedTo]);
  }, []);

  const canUserDeleteProject = useMemo(() => {
    return permissionsService.userHasPermissionTo(DeleteProject)
      || (permissionsService.userHasPermissionTo(DeleteProjectIfLead) && currentProjectStorage.currentProject.lead.id === currentUserService.currentUser.id);
  }, [currentProjectStorage.currentProject.lead.id]);

  const menuItems = {
    staticItems: projectMenuItems.getStaticItems(),
  };

  const hasCustomProperties = useMemo(() => {
    return !!currentProjectStorage.currentProject.customPropertyValues.length;
  }, [currentProjectStorage.currentProject.customPropertyValues.length]);

  const handleEditClick = () => {
    history.push(`/projects/${currentProjectStorage.currentProject.id}/edit`);
  };

  const handleDeleteProject = useCallback(async () => {
    try {
      await currentProjectStorage.deleteProject();
      history.push("/projects");
      snackbarNotificationsService.showSuccess("ViewProject.delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.project.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [currentProjectStorage, handleErrorInSnackbar, history]);

  useEffect(() => {
    breadcrumbsStorage.setEntityNames([{
      entity: EntityTypeFromPath.Project,
      name: `${currentProjectStorage.currentProject.name}`
    }]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [currentProjectStorage.currentProject.name, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewProjectPermission, ViewProjectAssignedTo]}
      titleKey="ViewProject.title"
      titleContext={{ projectName: currentProjectStorage.currentProject.name }}
      id="view-project"
      menuItems={menuItems}
      isLoading={currentProjectStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton disabled={!canUserEditProject} onClick={handleEditClick} />
          <DeleteButton
            disabled={!canUserDeleteProject}
            confirmationAction={handleDeleteProject}
            confirmationModalTitleKey="ViewProject.delete"
            confirmationModalSubtitleKey="ViewProject.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataSection labelKey="general.projects.generic-details">
        <SubSection>
          <DisplayProjectName name={currentProjectStorage.currentProject.name} />
          <DisplayProjectStatus status={currentProjectStorage.currentProject.status} />
          <DisplayProjectLead lead={currentProjectStorage.currentProject.lead} />
        </SubSection>
        <SubSection>
          <SubSection>
            <DisplayProjectFormat format={currentProjectStorage.currentProject.format} />
            <DisplayProjectCodeSystem
              codeSystem={currentProjectStorage.currentProject.codeSystem}
            />
            <DisplayCoordinatesSystem
              coordinateSystem={currentProjectStorage.currentProject.gisCoordinates || null}
            />
          </SubSection>
        </SubSection>
        <SubSection>
          <DisplayAvailableFrom date={currentProjectStorage.currentProject.availableFrom} />
          <DisplayDeadline date={currentProjectStorage.currentProject.deadline} />
          {featureFlagsStorage.isFeatureEnabled(Flags.Show_Page_Report_Templates) && (
            <DisplayReportTemplate template={currentProjectStorage.currentProject.reportTemplate} />
          )}
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentProjectStorage.currentProject.created} />
          <DisplayCreatedBy createdBy={currentProjectStorage.currentProject.createdBy.name} />
        </SubSection>
      </DataSection>
      {hasCustomProperties && (
        <CustomProjectPropertiesSection
          values={currentProjectStorage.currentProject.customPropertyValues}
        />
      )}
      <DescriptionSection description={currentProjectStorage.currentProject.description} />
    </PageLayout>
  );
});
